import type {
  ReploShopifyProduct,
  ReploShopifyVariant,
} from "replo-runtime/shared/types";
import type { Context } from "replo-runtime/store/ReploVariable";
import type { ProductWithVariants } from "schemas/component";
import type {
  BundleConfig,
  BundleDiscountThreshold,
} from "schemas/generated/bundleDiscounts";

import React, { createContext, useContext, useEffect, useState } from "react";

import isEqual from "lodash-es/isEqual";
import {
  RenderEnvironmentContext,
  RuntimeHooksContext,
  ShopifyStoreContext,
  useRuntimeContext,
} from "replo-runtime/shared/runtime-context";
import { getProduct } from "replo-runtime/store/ReploProduct";

import { formatCurrencyWithShopifyMoneyFormat } from "../../../shared/liquid";
import { loadingProduct } from "../../utils/fakeProducts";

export interface BundleCartItem {
  productId: string;
  variantId: string;
  quantity: number;
}

interface BundleBuilderContextType {
  bundleId: string;
  isTiered: boolean;
  bundleTargetType: "products" | "variants";
  allowMultipleOfSameItem: boolean;
  bundleDiscountThresholds: BundleDiscountThreshold[];
  bundleCartItems: BundleCartItem[];
  bundleProductsWithVariants: ProductWithVariants[];
  selectedBundleDiscountThreshold: BundleDiscountThreshold;
  nextBundleDiscountThreshold: BundleDiscountThreshold | null;
  canAddVariantToBundle: (variantId: string) => boolean;
  addItemToBundle: (item: BundleCartItem) => void;
  removeItemFromBundle: (variantId: string) => void;
  updateItemQuantity: (variantId: string, quantity: number) => void;
  setSelectedBundleDiscountThreshold: (
    threshold: BundleDiscountThreshold,
  ) => void;
  formatPrice: (price: string) => void;
  clearBundle: () => void;
  isItemInBundle: (variantId: string) => boolean;
  getItemQuantity: (variantId: string) => number;
  getBundleTotals: () => { total: string; subTotal: string; savings: string };
  getItemsCount: () => number;
  isSelectedThresholdMet: () => boolean;
  getEmptySlots: () => number;
  productsData: Record<string, ReploShopifyProduct>;
  getProductData: (productId: string) => ReploShopifyProduct | undefined;
  getVariantData: (
    variantId: string,
    productId: string,
  ) => {
    product: ReploShopifyProduct | undefined;
    variant: ReploShopifyVariant | undefined;
  };
  addBundleToCart: () => Promise<void>;
  getBundleTotalQuantity: () => number;
}

const BundleBuilderContext = createContext<BundleBuilderContextType | null>(
  null,
);

export const useBundleBuilder = () => {
  const context = useContext(BundleBuilderContext);
  if (!context) {
    throw new Error(
      "useBundleBuilder must be used within a BundleBuilderProvider",
    );
  }
  return context;
};

interface BundleBuilderProviderProps {
  children: React.ReactNode;
  componentId: string;
  bundleConfig: BundleConfig;
  context: Context;
}

export const BundleBuilderProvider: React.FC<BundleBuilderProviderProps> = ({
  children,
  componentId,
  bundleConfig,
  context,
}) => {
  const { isEditorApp } = useRuntimeContext(RenderEnvironmentContext);
  const {
    activeCurrency,
    activeLanguage,
    activeCurrency: currencyCode,
    activeLanguage: language,
    moneyFormat,
    moneyWithCurrencyFormat,
    templateProduct,
  } = useRuntimeContext(ShopifyStoreContext);
  const products = useRuntimeContext(RuntimeHooksContext).useShopifyProducts();
  const isShopifyProductsLoading =
    useRuntimeContext(RuntimeHooksContext).useIsShopifyProductsLoading();

  // Create a storage key unique to this bundle component
  const storageKey = `bundle-cart-${componentId}`;

  const [bundleCartItems, setBundleCartItems] = useState<BundleCartItem[]>(
    () => {
      // NOTE (Matt 2025-04-25): We store bundleCartItems state in session storage
      // for shoppers who navigate away and come back.
      try {
        const stored = sessionStorage.getItem(storageKey);
        return stored ? JSON.parse(stored) : [];
      } catch {
        return [];
      }
    },
  );

  const [selectedBundleDiscountThreshold, setSelectedBundleDiscountThreshold] =
    useState<BundleDiscountThreshold>(bundleConfig!.thresholds[0]!);

  // NOTE (Matt 2025-04-25): This useEffect is useful exclusively in the editor to ensure that as the bundle configuration
  // changes, that any selections that may have been interacted with in preview mode (ie the bundle cart state or the selected
  // threshold) are updated along with the new bundle config.
  useEffect(() => {
    if (isEditorApp) {
      const doesSelectedBundleDiscountThresholdExist =
        bundleConfig.thresholds.some((threshold) =>
          isEqual(threshold, selectedBundleDiscountThreshold),
        );
      if (!doesSelectedBundleDiscountThresholdExist) {
        setSelectedBundleDiscountThreshold(bundleConfig!.thresholds[0]!);
      }
      const filteredBundleCartItems = bundleCartItems.filter(
        ({ productId: bundleCartItemProductId, variantId }) => {
          return bundleConfig.productsWithVariants.find(
            ({ productId, variantIds }) => {
              if (variantIds?.length) {
                return variantIds.includes(variantId);
              }
              return productId === bundleCartItemProductId;
            },
          );
        },
      );
      if (filteredBundleCartItems.length !== bundleCartItems.length) {
        setBundleCartItems(filteredBundleCartItems);
      }
    }
  }, [
    bundleConfig,
    bundleCartItems,
    selectedBundleDiscountThreshold,
    isEditorApp,
  ]);
  const { nextBundleDiscountThreshold, previousBundleDiscountThreshold } =
    React.useMemo(() => {
      if (!bundleConfig.isTiered) {
        return {
          nextBundleDiscountThreshold: null,
          previousBundleDiscountThreshold: null,
        };
      }
      const bundleCartTotal = bundleCartItems.reduce(
        (sum, item) => sum + item.quantity,
        0,
      );
      const nextIndex = bundleConfig.thresholds.findIndex(
        ({ quantity }) => quantity > bundleCartTotal,
      );
      if (!bundleConfig.thresholds[nextIndex]) {
        return {
          nextBundleDiscountThreshold: bundleConfig.thresholds.slice(-1)[0]!,
          previousBundleDiscountThreshold:
            bundleConfig.thresholds.slice(-1)[0]!,
        };
      }
      return {
        nextBundleDiscountThreshold: bundleConfig.thresholds[nextIndex],
        previousBundleDiscountThreshold:
          bundleConfig.thresholds[nextIndex - 1] ?? null,
      };
    }, [bundleConfig, bundleCartItems]);

  const productsData: Record<string, ReploShopifyProduct> =
    React.useMemo(() => {
      const productDataMap: Record<string, ReploShopifyProduct> = {};

      for (const product of bundleConfig.productsWithVariants) {
        const { productId } = product;
        const productData = getProduct(
          { productId, variantId: undefined },
          context,
          {
            products,
            isShopifyProductsLoading,
            templateProduct,
            currencyCode: activeCurrency,
            language: activeLanguage,
            moneyFormat,
            loadingProduct,
            moneyWithCurrencyFormat,
            productMetafieldValues: {},
            variantMetafieldValues: {},
            isEditor: isEditorApp,
            fakeProducts: [],
          },
        );
        if (productData) {
          productDataMap[productId] = productData;
        }
      }
      return productDataMap;
    }, [
      context,
      products,
      isShopifyProductsLoading,
      templateProduct,
      activeCurrency,
      activeLanguage,
      moneyFormat,
      moneyWithCurrencyFormat,
      isEditorApp,
      bundleConfig,
    ]);

  // Helper methods to get product and variant data
  const getProductData = React.useCallback(
    (productId: string) => {
      return productsData[productId];
    },
    [productsData],
  );

  const getVariantData = React.useCallback(
    (variantId: string, productId: string) => {
      const product = productsData[productId];
      const variant = product?.variants.find(
        ({ id }) => String(id) === String(variantId),
      );
      return { product, variant };
    },
    [productsData],
  );

  // Save cart to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(bundleCartItems));
  }, [bundleCartItems, storageKey]);

  // NOTE (Matt 2025-05-05): if allowMultipleOfSameItem is false, we need to check if a matching
  // item already exists in the bundle. If the targetType is products, then we need to ensure
  const canAddVariantToBundle = (variantId: string) => {
    const currentTotalQuantity = getBundleTotalQuantity();
    const targetBundleDiscountThreshold =
      nextBundleDiscountThreshold ?? selectedBundleDiscountThreshold;
    // If we're already at or above the max tier quantity, don't allow adding more
    if (currentTotalQuantity >= targetBundleDiscountThreshold.quantity) {
      return false;
    }

    return (
      bundleConfig.allowMultipleOfSameItem ||
      !bundleCartItems.find((item) => {
        const { product } = getVariantData(item.variantId, item.productId);
        return (
          item.variantId === variantId ||
          (bundleConfig.targetType === "products" &&
            product?.variants.find(({ id }) => String(id) === variantId))
        );
      })
    );
  };

  // Functions for manipulating the cart
  const addItemToBundle = (item: BundleCartItem) => {
    if (!canAddVariantToBundle(item.variantId)) {
      return;
    }
    setBundleCartItems((prev) => {
      const existingItemIndex = prev.findIndex(
        (ci) => String(ci.variantId) === String(item.variantId),
      );

      const newItems = [...prev];
      if (newItems[existingItemIndex]) {
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + item.quantity,
        };
        return newItems;
      } else {
        return [...newItems, item];
      }
    });
  };

  const removeItemFromBundle = (variantId: string) => {
    setBundleCartItems((prev) =>
      prev.filter((item) => String(item.variantId) !== String(variantId)),
    );
  };

  const updateItemQuantity = (variantId: string, quantity: number) => {
    setBundleCartItems((prev) =>
      prev.map((item) =>
        String(item.variantId) === String(variantId)
          ? { ...item, quantity: Math.max(1, quantity) }
          : item,
      ),
    );
  };

  const clearBundle = () => {
    setBundleCartItems([]);
  };

  const isItemInBundle = (variantId: string) => {
    return bundleCartItems?.some(
      (item) => String(item.variantId) === String(variantId),
    );
  };

  const getItemQuantity = (variantId: string) => {
    const item = bundleCartItems.find(
      (item) => String(item.variantId) === String(variantId),
    );
    return item ? item.quantity : 0;
  };

  const getBundleTotalQuantity = () => {
    return bundleCartItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getItemsCount = () => {
    return bundleCartItems.length;
  };

  const isSelectedThresholdMet = () => {
    const currentTotalQuantity = getBundleTotalQuantity();
    if (bundleConfig.isTiered) {
      return (
        !previousBundleDiscountThreshold ||
        currentTotalQuantity >= previousBundleDiscountThreshold.quantity
      );
    }
    return currentTotalQuantity >= selectedBundleDiscountThreshold.quantity;
  };

  const getEmptySlots = () => {
    const targetBundleDiscountThreshold =
      nextBundleDiscountThreshold ?? selectedBundleDiscountThreshold;

    return Math.max(
      0,
      targetBundleDiscountThreshold.quantity - getBundleTotalQuantity(),
    );
  };

  const addBundleToCart = async () => {
    if (!isSelectedThresholdMet() || bundleCartItems.length === 0) {
      return;
    }

    const items = bundleCartItems.map((item) => ({
      id: Number(item.variantId),
      quantity: item.quantity,
      properties: {
        _reploBundleId: componentId,
      },
    }));

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      throw new Error("Failed to add items to cart");
    }

    clearBundle();

    // biome-ignore lint/style/noRestrictedGlobals: this is a placeholder until we allow configuring 'post-event' actions.
    window.location.href = "/cart";
  };

  const formatPrice = (price: string) => {
    return formatCurrencyWithShopifyMoneyFormat(price, {
      currencyCode: currencyCode ?? "USD",
      language: language ?? "en-US",
      moneyFormat: moneyFormat ?? null,
      moneyWithCurrencyFormat: moneyWithCurrencyFormat ?? null,
      showCents: true,
    });
  };

  const getBundleTotals = () => {
    if (bundleCartItems.length < 1) {
      return {
        subTotal: "0",
        savings: "0",
        total: "0",
      };
    }
    const subTotal = bundleCartItems.reduce(
      (num, { productId, variantId, quantity }) => {
        const { variant } = getVariantData(variantId, productId);
        if (variant) {
          num += Number(variant.rawPrice) * quantity;
        }
        return num;
      },
      0,
    );

    const targetBundleDiscountThreshold =
      previousBundleDiscountThreshold ?? selectedBundleDiscountThreshold;

    const { type: discountType, value } =
      targetBundleDiscountThreshold.discount;
    const savings =
      discountType === "fixedAmount" ? value : (subTotal * value) / 100;

    const total = subTotal - savings;
    return {
      subTotal: formatPrice(subTotal.toString()),
      savings: formatPrice(savings.toString()),
      total: formatPrice(total.toString()),
    };
  };

  const value: BundleBuilderContextType = {
    bundleId: componentId,
    bundleTargetType: bundleConfig.targetType,
    allowMultipleOfSameItem: bundleConfig.allowMultipleOfSameItem,
    isTiered: Boolean(bundleConfig.isTiered),
    bundleDiscountThresholds: bundleConfig.thresholds,
    bundleCartItems,
    bundleProductsWithVariants: bundleConfig.productsWithVariants,
    selectedBundleDiscountThreshold,
    nextBundleDiscountThreshold,
    canAddVariantToBundle,
    addItemToBundle,
    removeItemFromBundle,
    updateItemQuantity,
    setSelectedBundleDiscountThreshold,
    clearBundle,
    isItemInBundle,
    formatPrice,
    getItemQuantity,
    getBundleTotals,
    getItemsCount,
    isSelectedThresholdMet,
    getEmptySlots,
    productsData,
    getProductData,
    getVariantData,
    addBundleToCart,
    getBundleTotalQuantity,
  };

  return (
    <BundleBuilderContext.Provider value={value}>
      {children}
    </BundleBuilderContext.Provider>
  );
};
